import { Observable } from '@apollo/client';
import { ErrorLink } from "@apollo/client/link/error";
import { getMainDefinition } from '@apollo/client/utilities';
import { refreshToken } from './client';
import { setAuthStore, clearAuthVerified } from './sessionStore';
import { endKeycloakSession } from './signOut';
import { extractErrorCode, parseGraphQLError } from './errorMessages';
import { logger } from '../../lib/logger';
 
// --- Error classifiers ---
//
// Server contract (index.js): every auth-path failure is thrown via throwAuthError()
// as `new GraphQLError(devMessage, { extensions: { code: 'UNAUTHENTICATED', errorCode } })`.
// formatError then behaves differently depending on SHOW_ERRORS:
//   - SHOW_ERRORS=true (dev):  extensions pass through untouched, so extensions.errorCode
//     (e.g. 'POR-003', 'POR-004', 'DUP-001') is present, but `message` is whatever the
//     server happened to pass in (for TOKEN_EXPIRED that's the raw jsonwebtoken message,
//     e.g. "jwt expired" - no code prefix at all).
//   - SHOW_ERRORS=false (prod, the default): extensions is collapsed to just
//     `{ code: 'UNAUTHENTICATED' }` (errorCode is dropped), and `message` becomes the bare
//     code string itself (e.g. "POR-004").
// Reading extensions.code alone can't tell POR-003/POR-004/DUP-001 apart (they all carry
// 'UNAUTHENTICATED'), and reading err.message alone breaks in dev mode for anything whose
// devMessage isn't code-prefixed. getErrorCode() below combines both sources so the specific
// code resolves correctly in either mode.
const getErrorCode = (err) => err?.extensions?.errorCode ?? extractErrorCode(err?.message ?? '');

const isTerminationError = (err) =>
  err?.message === 'Session terminated — operation cancelled';

const isSupersededSession = (err) => {
  const code = getErrorCode(err);
  return (
    code === 'DUP-001' ||
    // Message-substring fallback: defense-in-depth for an error that never went through
    // this server's formatError (a proxy, a different service, a stale deployed bundle).
    err.message.includes('superseded') ||
    err.message.includes('duplicate')
  );
};

const isAuthError = (err) => {
  const code = getErrorCode(err);
  return (
    code === 'POR-003' ||
    code === 'POR-004' || // TOKEN_EXPIRED - routine expiry, still handled via silent refresh+retry
    // extensions.code is the same 'UNAUTHENTICATED' marker for every auth-path failure the
    // server throws (see throwAuthError in index.js), so any errorCode we don't explicitly
    // enumerate yet still gets routed to refresh-and-retry instead of silently falling
    // through to the unhandled-error bucket below. isSupersededSession is checked first in
    // handleGraphQLError's dispatch, so DUP-001 (which also carries 'UNAUTHENTICATED') is
    // already routed to sign-out before this runs.
    err?.extensions?.code === 'UNAUTHENTICATED' ||
    err.message.includes('authentication') ||
    err.message.includes('Session error') ||
    err.message.includes('Invalid authorization')
  );
};

const isPermissionError = (err) =>
  err.message === 'No access control specified for attempted operation.' ||
  err.message === 'Unauthorized';

const isNoResults = (err) => {
  const code = getErrorCode(err);
  return code === 'POR-002' || err.message.includes('Nothing found');
};

// RBAC: distinguishes background data fetches from explicit user actions.
// In this codebase, mutations are always triggered by a deliberate click
// (Save/Delete/Assign/Submit), while queries mostly fire on component mount
// regardless of whether the user did anything - and with most UI now
// permission-gated, a query hitting FORBIDDEN is far more likely to be a
// component still mounted somewhere hidden than something the user needs
// to be told about. getMainDefinition reads the operation type straight
// off the query document, since Apollo Link's `operation` object doesn't
// expose it directly.
const isMutationOperation = (operation) => {
  try {
    const definition = getMainDefinition(operation.query);
    return definition?.kind === 'OperationDefinition' && definition.operation === 'mutation';
  } catch {
    return false;
  }
};
 
// --- Handlers ---
 
export const safeEndSession = (reason) => {
  void endKeycloakSession(reason).catch(() => {
    logger.error(`endKeycloakSession failed: ${reason}`);
    window.location.replace('/signout');
  });
};
 
const handleSupersededSession = () => {
  clearAuthVerified();
  sessionStorage.setItem('unauthenticated', 'true');
  window.dispatchEvent(new Event('session-terminated'));
  safeEndSession('superseded session');
};
 
const handleTokenRefresh = (operation, forward) => {
  const { hasAttemptedRefresh } = operation.getContext();
 
  if (hasAttemptedRefresh) {
    logger.debug('[auth] Token refresh did not resolve auth error, signing out');
    safeEndSession('refresh token invalid or expired');
    return new Observable(observer => { observer.complete(); });
  }
 
  return new Observable(observer => {
    refreshToken()
      .then(response => {
        if (response?.access_token) {
          setAuthStore(Object.assign({}, response));
          operation.setContext({
            hasAttemptedRefresh: true,
            headers: {
              ...operation.getContext().headers,
              authorization: `Bearer ${response.access_token}`
            }
          });
          forward(operation).subscribe(observer);
        } else if (response?.error) {
          logger.debug('[auth] refresh failed:', response.error_description);
          safeEndSession('refresh token invalid or expired');
          observer.complete();
        } else {
          safeEndSession('token refresh failed');
          observer.complete();
        }
      })
      .catch(() => {
        safeEndSession('token refresh error');
        observer.complete();
      });
  });
};
 
// --- Error classifier -> handler dispatch ---
 
const handleGraphQLError = (err, operation, forward, showError) => {
  //Turn this on for debugging error messages in dev
  //logger.debug(err);
 
  if (isSupersededSession(err)) { handleSupersededSession(); return; }
  if (isAuthError(err))         { return handleTokenRefresh(operation, forward); }

  if (isPermissionError(err)) {
    // RBAC: only surface a toast for mutations - a permission error on a
    // background query (most operations, since they fire on mount
    // regardless of user intent) isn't something the user did anything to
    // trigger, so a toast there just reads as a confusing false alarm,
    // especially right after login for roles missing certain permissions.
    // Mutations are always explicit actions (Save/Delete/Assign/Submit),
    // so a permission error there is genuinely actionable feedback.
    if (isMutationOperation(operation)) {
      showError({ msg: 'Insufficient permission to perform operation.', id: 'permsError' });
    } else {
      logger.debug(`Permission error on background query "${operation.operationName}" - not shown to user.`);
    }
    return;
  }

  if (isNoResults(err))         { console.log('No Results'); return; }
 
  // All other errors - show user-friendly message
  // We can show the toast in the future after testing all errors 
  // showError({ msg: parseGraphQLError(err) });
  // console.log(parseGraphQLError(err))
  logger.debug('Unhandled GQL error:', parseGraphQLError(err));
};
 
// --- Link factory ---
 
export const createErrorLink = (error, env) => new ErrorLink(({ graphQLErrors, networkError, operation, forward }) => {
  // Ignore errors from the termination guard - session is already being torn down
  if (isTerminationError(networkError)) return;
 
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      if (isTerminationError(err)) return;
      const result = handleGraphQLError(err, operation, forward, error);
      if (result) return result; // Observable returned from handleTokenRefresh
    }
  }
 
  if (networkError) {
    error({ msg: 'A network error occurred. Please check your connection and try again.' });
  }
});
